import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField } from "common";
import React from "react";
import { useMutation } from "react-query";
import { number, object } from "yup";
import { ConfirmDataRetentionPolicyModal } from "./ConfirmDataRetentionPolicyModal";
import { api } from "@/api/api";
import { NumberInlineValuePopover } from "@/components/Common/NumberInlineValuePopover";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useSeller } from "@/hooks/useAccount";
import { useConfirmationModal } from "@/hooks/useConfirmationModal";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";

const MIN_RETENTION_DAYS = 180;

const parseDurationDays = (iso: string | null | undefined): number => {
    if (!iso) {
        return MIN_RETENTION_DAYS;
    }

    const match = iso.match(/^P(\d+)D$/);
    return match ? Number.parseInt(match[1], 10) : MIN_RETENTION_DAYS;
};

const toDurationIso = (days: number): string => `P${days}D`;

const validationSchema = object().shape({
    retentionDays: number()
        .typeError("Must be a number")
        .required("Retention duration is required")
        .integer("Must be a whole number")
        .min(MIN_RETENTION_DAYS, `Must be at least ${MIN_RETENTION_DAYS} days`),
});

interface FormValues {
    isEnabled: boolean;
    retentionDays: number;
}

export const CustomerDataRetentionSettings = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const dataRetentionPreference = seller.preferences?.dataRetention;
    const wasAlreadyEnabled = dataRetentionPreference?.enabled ?? false;
    const existingDuration = dataRetentionPreference?.duration;

    const { ConfirmationModal, confirm } = useConfirmationModal(ConfirmDataRetentionPolicyModal);
    const [isDaysOpen, { toggle: toggleDays }] = useVisibility(false);

    const { mutateAsync: updateDataRetentionPreferences, isLoading } = useMutation(
        async (payload: { enabled: boolean; duration: string | null }) => {
            try {
                await api.post(`/users/${seller.id}/preferences/dataRetention`, payload);
                invalidate("sellers", seller.id);
                flash.success("Successfully updated customer data retention settings");
            } catch {
                flash.danger("Error updating customer data retention settings");
            }
        },
    );

    const handleSubmit = async (values: FormValues) => {
        if (values.isEnabled) {
            const confirmed = await confirm();
            if (!confirmed) {
                return;
            }
        }

        const duration = values.isEnabled ? toDurationIso(values.retentionDays) : null;
        return updateDataRetentionPreferences({ enabled: values.isEnabled, duration });
    };

    const initialRetentionDays = parseDurationDays(existingDuration);

    const initialValues: FormValues = {
        isEnabled: wasAlreadyEnabled,
        retentionDays: initialRetentionDays,
    };

    return (
        <>
            <XolaFormik
                enableReinitialize
                validationSchema={validationSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit: submitForm, setFieldValue, values, errors, dirty, handleReset }) => (
                    <ContentLayout isWide>
                        <ContentLayout.Section
                            title="Data Retention Policy"
                            description="Manage how long personally identifiable information (PII) is stored"
                        >
                            <div className="mb-6 rounded bg-blue-lighter p-4">
                                This refers to any data that can be used to uniquely identify a customer, such as name,
                                contact details, and billing information.
                            </div>

                            <Switch.Group>
                                <FormField name="retentionDays">
                                    <span className="inline-flex items-center gap-x-2 text-gray-darker">
                                        <Switch
                                            size="medium"
                                            isChecked={values.isEnabled}
                                            onChange={(value: boolean) => setFieldValue("isEnabled", value)}
                                        />
                                        Automatically mask personally identified information
                                        {values.isEnabled && (
                                            <>
                                                <NumberInlineValuePopover
                                                    autoSelectOnClick
                                                    isOpen={isDaysOpen}
                                                    value={values.retentionDays}
                                                    text={<var data-var="retentionDays">{values.retentionDays}</var>}
                                                    error={errors.retentionDays as string | undefined}
                                                    min={MIN_RETENTION_DAYS}
                                                    step="1"
                                                    onOpenChange={toggleDays}
                                                    onChange={(value) => setFieldValue("retentionDays", value)}
                                                />
                                                days after purchase fulfillment.
                                            </>
                                        )}
                                    </span>
                                </FormField>
                            </Switch.Group>
                        </ContentLayout.Section>

                        <ContentLayout.Footer>
                            <SubmitButton isLoading={isLoading} disabled={!dirty} onClick={submitForm}>
                                {dirty ? "Save" : "Saved"}
                            </SubmitButton>
                            <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                )}
            </XolaFormik>
            <ConfirmationModal />
        </>
    );
};
